/**
 * Ranked search over the image catalog — operating systems and marketplace apps
 * in ONE index, because a customer typing "wordpress" on the operating-system
 * tab (or "ubuntu" on the marketplace one) was told nothing matched.
 *
 * What it has to answer is how people actually look for software they have not
 * met: by what they want done ("whatsapp", "loja virtual", "vpn", "pabx"), in
 * Portuguese or English, with or without accents, with the product's own
 * punctuation or without it ("node red", "rocketchat"), and misspelled
 * ("wordpres"). The caller supplies the words — the localized shelf, the
 * tagline and the intent keywords come from the locale files — so this module
 * stays pure and imports nothing, and `node --test` can pin its ranking.
 *
 * ~130 documents: a linear scan per keystroke is well under a millisecond, and a
 * fuzzy-search dependency would buy nothing but weight.
 */

export type TemplateSearchField = 'name' | 'code' | 'version' | 'keywords' | 'shelf' | 'tagline'

export type TemplateSearchDocument = {
  /** Returned in the hits as given — the template's code. */
  id: string
  name: string
  code: string
  version?: string | null
  /** Comma- or space-separated intent words, already localized. */
  keywords?: string | null
  /** The shelf or OS family, already localized. */
  shelf?: string | null
  tagline?: string | null
  /** Lower wins a tie (the featured position). */
  rank?: number
}

export type TemplateSearchHit = { id: string; score: number }

type IndexedField = { text: string; compact: string; words: string[] }
type IndexedDocument = {
  doc: TemplateSearchDocument
  fields: Partial<Record<TemplateSearchField, IndexedField>>
  /** The keywords one phrase each, in the order the copy lists them. */
  phrases: IndexedField[]
}
export type TemplateSearchIndex = { entries: IndexedDocument[] }

/**
 * A name match outranks everything a keyword can say; the tagline is prose and
 * only breaks ties. The code is nearly a name ("wg-easy", "k3s").
 */
const FIELD_WEIGHT: Record<TemplateSearchField, number> = {
  name: 10,
  code: 8,
  version: 6,
  keywords: 5,
  shelf: 3,
  tagline: 2,
}

/** Typos are forgiven only where a word is a NAME a customer tries to spell. */
const FUZZY_FIELDS: ReadonlySet<TemplateSearchField> = new Set(['name', 'code', 'keywords'])

/**
 * A match INSIDE a word only where products are named, and only at the END of
 * one: the second half of a compound name is how people type it ("sql" for
 * PostgreSQL, "panel" for CyberPanel, "pbx" for FreePBX). In the middle of a
 * word, or anywhere in prose, it finds nonsense: "erp" is inside "cyberpanel"
 * and "enterprise".
 */
const SUBSTRING_FIELDS: ReadonlySet<TemplateSearchField> = new Set(['name', 'code'])

/**
 * Words that carry no intent. "gerenciador de senhas" must not require every
 * result to contain "de"; a query made only of them is kept as typed.
 */
const STOPWORDS: ReadonlySet<string> = new Set([
  'a', 'as', 'o', 'os', 'um', 'uma', 'de', 'da', 'das', 'do', 'dos', 'e', 'em', 'no', 'na',
  'nos', 'nas', 'para', 'pra', 'por', 'com', 'the', 'an', 'of', 'for', 'and', 'to', 'in',
  'with', 'on',
])

/**
 * Lowercase, accents stripped, punctuation to spaces — except the dot, which
 * versions need ("24.04"). `\p{M}` rather than a code-point range: the marks are
 * what NFD separates out, whichever script they belong to.
 */
export function foldSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function words(folded: string): string[] {
  return folded
    .split(' ')
    .map((word) => word.replace(/^\.+|\.+$/g, ''))
    .filter(Boolean)
}

/** The query's words, minus the ones that carry no intent (unless that is all of them). */
export function searchQueryTokens(query: string): string[] {
  const tokens = words(foldSearchText(query))
  const meaningful = tokens.filter((token) => !STOPWORDS.has(token))
  return meaningful.length > 0 ? meaningful : tokens
}

function indexField(value: string | null | undefined): IndexedField | undefined {
  if (!value) return undefined
  const text = foldSearchText(value)
  if (!text) return undefined
  return { text, compact: text.replace(/[ .]/g, ''), words: words(text) }
}

export function buildTemplateSearchIndex(documents: readonly TemplateSearchDocument[]): TemplateSearchIndex {
  return {
    entries: documents.map((doc) => ({
      doc,
      fields: {
        name: indexField(doc.name),
        code: indexField(doc.code),
        version: indexField(doc.version),
        keywords: indexField(doc.keywords),
        shelf: indexField(doc.shelf),
        tagline: indexField(doc.tagline),
      },
      phrases: (doc.keywords ?? '')
        .split(',')
        .map((phrase) => indexField(phrase))
        .filter((field): field is IndexedField => !!field),
    })),
  }
}

/**
 * How much a keyword counts by where the copy lists it. The copy lists what an
 * app IS first ("loja virtual" leads WooCommerce's list and is eighth in Odoo's),
 * so "loja virtual" should find the store before the ERP that also has one —
 * without this every keyword match tied and the alphabet decided.
 */
function phraseWeight(position: number): number {
  return Math.max(0.55, 1 - position * 0.05)
}

/**
 * Optimal-string-alignment distance, bounded: past `limit` the exact figure is
 * irrelevant, so it stops as soon as a row cannot come back under it.
 */
function editDistance(a: string, b: string, limit: number): number {
  if (Math.abs(a.length - b.length) > limit) return limit + 1
  let previousPrevious: number[] = []
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const current = [i]
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let value = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, previousPrevious[j - 2]! + 1)
      }
      current.push(value)
      rowMin = Math.min(rowMin, value)
    }
    if (rowMin > limit) return limit + 1
    previousPrevious = previous
    previous = current
  }
  return previous[b.length]!
}

function tokenScore(token: string, field: IndexedField, fuzzy: boolean, substring: boolean): number {
  if (field.words.includes(token)) return 100
  if (field.words.some((word) => word.startsWith(token))) return 70
  // "nodered" for "Node-RED", "rocketchat" for "Rocket.Chat".
  if (field.compact.startsWith(token)) return 60
  if (substring && token.length >= 3 && field.words.some((word) => word.endsWith(token))) return 35
  if (fuzzy && token.length >= 4) {
    const limit = token.length >= 8 ? 2 : 1
    for (const word of field.words) {
      if (word.length < 3) continue
      if (editDistance(token, word, limit) <= limit) return 20
      // A word typed part-way with a slip in it ("wordpres", "evolutin").
      if (word.length > token.length && editDistance(token, word.slice(0, token.length), limit) <= limit) {
        return 20
      }
    }
  }
  return 0
}

/**
 * Every token must match SOMETHING in a document (a query narrows, it never
 * widens). A token scores its best field in full and every other field that
 * also has it at a quarter — a word in the name AND the tagline is better
 * evidence than a word in one of them. Keywords count by where the copy lists
 * them, and a query that IS one of an app's keyword phrases ("loja virtual")
 * counts once more. A document whose name IS the query, or starts with it, goes
 * first. Ties go to the featured order, then the name.
 */
export function searchTemplateIndex(index: TemplateSearchIndex, query: string): TemplateSearchHit[] {
  const tokens = searchQueryTokens(query)
  if (tokens.length === 0) return []
  const whole = foldSearchText(query)

  const hits: { hit: TemplateSearchHit; rank: number; name: string }[] = []
  for (const entry of index.entries) {
    let score = 0
    let matchedAll = true
    for (const token of tokens) {
      const byField: number[] = []
      for (const [key, field] of Object.entries(entry.fields) as [TemplateSearchField, IndexedField | undefined][]) {
        if (!field) continue
        if (key === 'keywords') {
          let bestPhrase = 0
          entry.phrases.forEach((phrase, position) => {
            const raw = tokenScore(token, phrase, true, false)
            if (raw > 0) bestPhrase = Math.max(bestPhrase, raw * phraseWeight(position))
          })
          if (bestPhrase > 0) byField.push(bestPhrase * FIELD_WEIGHT.keywords)
          continue
        }
        const raw = tokenScore(token, field, FUZZY_FIELDS.has(key), SUBSTRING_FIELDS.has(key))
        if (raw > 0) byField.push(raw * FIELD_WEIGHT[key])
      }
      if (byField.length === 0) {
        matchedAll = false
        break
      }
      byField.sort((a, b) => b - a)
      score += byField[0]! + byField.slice(1).reduce((sum, value) => sum + value / 4, 0)
    }
    if (!matchedAll) continue
    const name = entry.fields.name?.text ?? ''
    if (name === whole) score += 2000
    else if (name.startsWith(whole)) score += 500
    const phraseAt = entry.phrases.findIndex((phrase) => phrase.text === whole)
    if (phraseAt !== -1 && tokens.length > 1) score += 300 * phraseWeight(phraseAt)
    hits.push({
      hit: { id: entry.doc.id, score },
      rank: entry.doc.rank ?? Number.POSITIVE_INFINITY,
      name: entry.doc.name,
    })
  }

  return hits
    .sort((a, b) => b.hit.score - a.hit.score || a.rank - b.rank || a.name.localeCompare(b.name))
    .map(({ hit }) => hit)
}

/**
 * `text` cut into segments with the query's words marked, for highlighting a
 * result's name. Folded character by character so an accent or a capital in the
 * original never shifts a mark onto the wrong letters.
 */
export function highlightSegments(text: string, query: string): { text: string; match: boolean }[] {
  const tokens = searchQueryTokens(query).filter((token) => token.length > 0)
  if (!text || tokens.length === 0) return [{ text, match: false }]

  const chars = Array.from(text)
  let folded = ''
  const owner: number[] = []
  chars.forEach((char, index) => {
    const piece = char.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase()
    folded += piece
    // One owner per UTF-16 unit: `indexOf` below counts units, not characters.
    for (let unit = 0; unit < piece.length; unit++) owner.push(index)
  })

  const marked = new Array<boolean>(chars.length).fill(false)
  for (const token of tokens) {
    const at = folded.indexOf(token)
    if (at === -1) continue
    for (let i = at; i < at + token.length; i++) marked[owner[i]!] = true
  }

  const segments: { text: string; match: boolean }[] = []
  chars.forEach((char, index) => {
    const match = marked[index]!
    const last = segments[segments.length - 1]
    if (last && last.match === match) last.text += char
    else segments.push({ text: char, match })
  })
  return segments
}
